import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { Card, FormGroup, showSnackbar } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";
import { CheckRounded, CloseRounded } from "@mui/icons-material";

import DataFormat from "components/utils/DataFormat";
import { useConfirm } from "components/hooks/useConfirm";

import { Language } from "utils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";

import { AgreementTypeEnum, Contract, ContractOption, IndexationType, TimeUnit } from "__generated__/graphql";
import { gql } from "__generated__/gql";

const ContractOptionDetails = ({ contract, refetch }: {
	contract: Pick<Contract, "id" | "contractOptions" | "isEditable"> & {
		agreements: {
			type: AgreementTypeEnum | null;
			calculationMethod?: { id: number, timeUnit?: TimeUnit | null | undefined } | null | undefined;
		}[];
	};
	refetch: () => void;
}) => {

	const contractOptions = contract.contractOptions ?? [];

	const priceUnit = contract.agreements
		.find(agreement => agreement.type === AgreementTypeEnum.Rent)
		?.calculationMethod?.timeUnit ?? "UNIT";

	return (
		<div style={{ backgroundColor: "#fafafa", padding: 24 }}>
			<div className="contract-options">
				{contractOptions.length < 1 && (
					<p style={{ margin: 0 }}>{Language("NO_CONTRACT_OPTIONS_FOUND")}</p>
				)}

				{contractOptions.map((option, index) => (
					<div style={{ marginBottom: index === contractOptions.length - 1 ? 0 : 24 }} key={option.id}>
						<Option
							contractId={contract.id}
							option={option}
							priceUnit={priceUnit}
							index={index}
							canEditContract={contract.isEditable ?? false}
							refetch={refetch} />
					</div>
				))}
			</div>
		</div>
	);
};

const Option = ({ contractId, option, priceUnit, index, canEditContract, refetch }: {
	contractId: number;
	option: ContractOption;
	priceUnit: TimeUnit | "UNIT";
	index: number;
	canEditContract: boolean;
	refetch: () => void;
}) => {

	const confirm = useConfirm();

	const takeOptionActionEnabled = !canEditContract && index === 0 && option.optionTaken === null;

	const [ takeOptionMutation ] = useMutation(TAKEN_OPTION_MUTATION);

	return (
		<Card
			title={<FormattedDateWindow from={option.validFrom} to={option.validUntil} format="ll" />}
			collapsable
			color={option.optionTaken ? "#F39200" : "gray"}
			defaultCollapsed
			actions={[ {
				tooltip: intl.formatMessage({ id: "TAKE_OPTION_NO_BUTTON" }),
				onClick: () => {
					confirm({
						type: "WARNING",
						title: intl.formatMessage({ id: "TAKE_OPTION_NO_DIALOG_BODY" }),
					}).then(() => {
						takeOptionMutation({
							variables: {
								contractId: contractId,
								takeOption: false,
							},
						}).then(() => {
							refetch();
						});
					});
				},
				icon: <CloseRounded color="action" />,
				hidden: !takeOptionActionEnabled,
			}, {
				tooltip: intl.formatMessage({ id: "TAKE_OPTION_YES_BUTTON" }),
				onClick: () => {
					confirm({
						type: "WARNING",
						title: intl.formatMessage({ id: "TAKE_OPTION_YES_DIALOG_BODY" }),
					}).then(() => {
						takeOptionMutation({
							variables: {
								contractId: contractId,
								takeOption: true,
							},
						}).then((response) => {
							showSnackbar(intl.formatMessage({ id: "NEW_OPTION_CONTRACT_CREATED" }) + " " + (response.data?.takeOption?.contractNumber ?? ""), { variant: "success" });
							refetch();
						});
					});
				},
				icon: <CheckRounded />,
				hidden: !takeOptionActionEnabled,
			} ]}>
			<Grid container spacing={3}>
				<Grid size={6}>
					<FormGroup label={Language("DESCRIPTION")} overflow="hidden">
						{option.description ?? "-"}
					</FormGroup>

					<FormGroup label={Language("OPTION_TAKEN")} overflow="hidden">
						{typeof option.optionTaken === "boolean" ? DataFormat.booleanAsText(option.optionTaken) : intl.formatMessage({ id: "OPEN" })}
					</FormGroup>

					<FormGroup label={Language("INDEXATION_TYPE")} overflow="hidden">
						{intl.formatMessage({ id: option.indexationType })}
					</FormGroup>

					<FormGroup label={Language("CONTRACT_NOTICE_PERIOD")} overflow="hidden">
						{option.noticePeriod ? intl.formatMessage({ id: `CONTRACT_NOTICE_PERIOD_${option.noticePeriod}` }) : "-"}
					</FormGroup>
				</Grid>

				<Grid size={6}>
					{option.indexationType !== IndexationType.None && (
						<>
							<FormGroup label={Language("INDEXATION_DATE")} overflow="hidden">
								<FormattedDate date={option.indexationDate} format="ll" />
							</FormGroup>

							<FormGroup label={Language("INDEXATION_AMOUNT")} overflow="hidden">
								{option.indexationAmount ?? "-"}
							</FormGroup>
						</>
					)}

					<FormGroup label={Language(`PRICE_PER_${priceUnit}`)} overflow="hidden">
						{option.pricePerUnit}
					</FormGroup>

					{option.indexationType !== IndexationType.None && (
						<>
							<FormGroup label={Language(`INDEXED_PRICE_PER_${priceUnit}`)} overflow="hidden">
								{option.indexedPricePerUnit ?? "-"}
							</FormGroup>

							<FormGroup label={Language("USE_END_PRICE_OF_THE_CONTRACT")} overflow="hidden">
								{ DataFormat.boolean(option.useEndPriceOfTheContract) }
							</FormGroup>
						</>
					)}
				</Grid>
			</Grid>
		</Card>
	);
};

const TAKEN_OPTION_MUTATION = gql(`#graphql
	mutation takeOption($contractId: Long!, $takeOption: Boolean!) {
		takeOption(contractId: $contractId, takeOption: $takeOption) {
			id
			contractNumber
		}
	}
`);

export default ContractOptionDetails;
